import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Paper,
  FormGroup,
  FormControlLabel,
  Switch
} from '@mui/material';
import logCollectionService from '../../services/LogCollectionService';

const LogCollector = () => {
  const [collectionStatus, setCollectionStatus] = useState(() => logCollectionService.getCollectionStatus());
  const [showLogs, setShowLogs] = useState(false);
  const [captureNetwork, setCaptureNetwork] = useState(false);
  const logsRef = useRef(null);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCollectionStatus(logCollectionService.getCollectionStatus());
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);
  
  const toggleCollection = () => {
    if (collectionStatus.isCollecting) {
      logCollectionService.stopCollection();
    } else {
      logCollectionService.startCollection({ captureNetwork });
    }
    
    setCollectionStatus(logCollectionService.getCollectionStatus());
  };
  
  const handleDownload = () => {
    logCollectionService.downloadLogs();
  };
  
  const getLogs = () => {
    return logCollectionService.getLogs().slice(-100);
  };
  
  useEffect(() => {
    if (showLogs && logsRef.current) {
      logsRef.current.scrollTop = logsRef.current.scrollHeight;
    }
  }, [showLogs, collectionStatus.logsCount]);

  const handleNetworkToggle = (e) => {
    setCaptureNetwork(e.target.checked);
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          Log Collection
        </Typography>
        <Chip 
          label={collectionStatus.isCollecting ? "Recording" : "Stopped"} 
          color={collectionStatus.isCollecting ? "success" : "default"} 
          variant="outlined" 
          size="small" 
        />
      </Box>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Collect logs to help diagnose issues. When enabled, all console messages and errors will be recorded for debugging purposes.
        </Typography>
        
        {collectionStatus.isCollecting && (
          <Typography variant="body2" color="text.secondary">
            Recording started: {new Date(collectionStatus.startTime).toLocaleTimeString()}
          </Typography>
        )}
      </Box>

      {!collectionStatus.isCollecting && (
        <FormGroup sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={captureNetwork}
                onChange={handleNetworkToggle}
                name="captureNetwork"
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Capture network requests (may cause issues with some features)
              </Typography>
            }
          />
        </FormGroup>
      )}
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Button
          variant={collectionStatus.isCollecting ? "outlined" : "contained"}
          size="small"
          onClick={toggleCollection}
          color={collectionStatus.isCollecting ? "error" : "primary"}
        >
          {collectionStatus.isCollecting ? "Stop Recording" : "Start Recording"}
        </Button>
        
        <Button
          variant="outlined"
          size="small"
          onClick={handleDownload}
          disabled={collectionStatus.logsCount === 0}
        >
          Download Logs
        </Button>
        
        <Button
          variant="text"
          size="small"
          onClick={() => setShowLogs(!showLogs)}
          disabled={collectionStatus.logsCount === 0}
        >
          {showLogs ? "Hide Logs" : "Show Logs"}
        </Button>
      </Box>
      
      {showLogs && (
        <Paper 
          variant="outlined" 
          sx={{ 
            height: 200, 
            overflow: 'auto', 
            p: 1, 
            mb: 1, 
            fontFamily: 'monospace',
            fontSize: '0.75rem'
          }}
          ref={logsRef}
        >
          {getLogs().map((log, i) => (
            <Box key={i} sx={{ 
              py: 0.5, 
              borderBottom: '1px dotted',
              borderColor: 'divider',
              color: log.level === 'error' ? 'error.main' : 
                     log.level === 'warn' ? 'warning.main' : 
                     log.level === 'network' ? 'info.main' : 'text.primary'
            }}>
              <Box component="span" sx={{ opacity: 0.7, mr: 1 }}>
                {new Date(log.timestamp).toLocaleTimeString()}
              </Box>
              <Box component="span" sx={{ 
                fontWeight: 'bold',
                mr: 1,
                textTransform: 'uppercase'
              }}>
                [{log.level}]
              </Box>
              {log.message}
            </Box>
          ))}
          
          {getLogs().length === 0 && (
            <Typography variant="body2" sx={{ p: 1, color: 'text.secondary' }}>
              No logs collected yet. Start recording to collect logs.
            </Typography>
          )}
        </Paper>
      )}
      
      {collectionStatus.logsCount > 0 && (
        <Typography variant="caption" color="text.secondary">
          {collectionStatus.logsCount} logs collected
        </Typography>
      )}
    </Box>
  );
};

export default LogCollector;